<template>
  <div v-loading="loading" class="talent-matrix-setting">
    <div v-if="matrix && matrix.setting">
      <div>
        <h3>Настройка осей</h3>
        <p>
          Выберите, какая оценка относятся к потенциалу, а какая к производительности.<br>
          Для построения матрицы будут использоваться последние полученные оценки сотрудников.<br>
          Также вы можете импортировать свои данные файлом в формате excel по шаблону.
        </p>
        <div class="setting-axis">
          <div class="axis">
            <p>Потенциал (ось Y)</p>
            <el-switch
              v-model="matrix.setting.potential_is_import"
              :active-value="true"
              :inactive-value="false"
              active-text="Импортировать через файл?"
              @change="updateSetting"
            >
            </el-switch>
            <div style="margin-top: 10px">
              <div v-if="!matrix.setting.potential_is_import">
                <eval-evaluations-select
                  v-model="matrix.setting.potential_evaluation_id"
                  :multiple="false"
                  @change="updateSetting"
                ></eval-evaluations-select>
                <talent-matrix-axis-formula-select
                  style="margin-top: 10px"
                  v-model="matrix.setting.potential_formula"
                  :multiple="false"
                  @change="updateSetting"
                ></talent-matrix-axis-formula-select>
              </div>
              <div v-else>
                <import-modal
                  style="margin-right: 10px"
                  entity="matrix_productivity_potential"
                  title="Импорт потенциала"
                  close-on-success
                  :data="{'matrix_id': $route.params.id, 'type': 'potential'}"
                  @import-succeed="importSucceed"
                >
                  <template v-slot:reference>
                    <el-tooltip
                      content="Импорт потенциала"
                      placement="top"
                      :open-delay="500"
                    >
                      <el-button
                        class="table-action-button"
                        round
                        size="mini"
                        icon="fas fa-file-import"
                        @click=""
                      >
                        Импорт в XLSX
                      </el-button>
                    </el-tooltip>
                  </template>
                </import-modal>
                <div style="margin-top: 15px">
                  <el-button
                    size="mini"
                    icon="fas fa-list-alt"
                    @click="openAxisUserValuesDialog('potential')"
                  >
                    Смотреть данные {{ matrix.import_potential_count }}
                  </el-button>
                </div>
              </div>
            </div>
          </div>
          <div class="axis">
            <p>Производительность (ось X)</p>
            <el-switch
              v-model="matrix.setting.productivity_is_import"
              :active-value="true"
              :inactive-value="false"
              active-text="Импортировать через файл?"
              @change="updateSetting"
            >
            </el-switch>
            <div style="margin-top: 10px">
              <div v-if="!matrix.setting.productivity_is_import">
                <eval-evaluations-select
                  v-model="matrix.setting.productivity_evaluation_id"
                  :multiple="false"
                  @change="updateSetting"
                ></eval-evaluations-select>
                <talent-matrix-axis-formula-select
                  style="margin-top: 10px"
                  v-model="matrix.setting.productivity_formula"
                  :multiple="false"
                  @change="updateSetting"
                ></talent-matrix-axis-formula-select>
              </div>
              <div v-else>
                <import-modal
                  style="margin-right: 10px"
                  entity="matrix_productivity_potential"
                  title="Импорт производительности"
                  close-on-success
                  :data="{'matrix_id': $route.params.id, 'type': 'productivity'}"
                  @import-succeed="importSucceed"
                >
                  <template v-slot:reference>
                    <el-tooltip
                      content="Импорт производительности"
                      placement="top"
                      :open-delay="500"
                    >
                      <el-button
                        class="table-action-button"
                        round
                        size="mini"
                        icon="fas fa-file-import"
                        @click=""
                      >
                        Импорт в XLSX
                      </el-button>
                    </el-tooltip>
                  </template>
                </import-modal>
                <div style="margin-top: 15px">
                  <el-button
                    size="mini"
                    icon="fas fa-list-alt"
                    @click="openAxisUserValuesDialog('productivity')"
                  >
                    Смотреть данные {{ matrix.import_productivity_count }}
                  </el-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div style="margin-top: 50px">
        <h3>Настройка видимости</h3>
        <p>Выберите, будут ли пользователи видеть всех сотрудников в матрице или только тех, кто доступен им по настройкам подчинённости.</p>
        <el-radio-group
          v-model="matrix.setting.visibility_all_users"
        >
          <el-radio
            :key="true"
            :label="true"
            border
            @change="updateSetting"
          >
            Все пользователи
          </el-radio>
          <el-radio
            :key="false"
            :label="false"
            border
            @change="updateSetting"
          >
            Только в пределах своей видимости по подчинённости
          </el-radio>
        </el-radio-group>
      </div>
      <div style="margin-top: 50px">
        <h3>Настройка доступа к просмотру матрицы</h3>
        <p>Выберите пользователей, которые смогут открыть матрицу по ссылке в режиме просмотра.</p>
        <share-talent-matrix-users-table style="width: 60%"></share-talent-matrix-users-table>
      </div>
      <div style="margin-top: 50px">
        <h3>Настройка размера матрицы</h3>
        <p>
          Выберите, какой размер будет у потенциала, а какой у производительности.<br>
          Минимальное значение - 1, максимальное значение - 5.
        </p>
        <div class="setting-size">
          <div class="size">
            <p>Потенциал (ось Y)</p>
            <div style="margin-top: 10px">
              <el-input
                placeholder="Ось Y"
                v-model.number="matrix.setting.potential_size"
                style="width: 100px"
                :min="1"
                :max="5"
                type="number"
                @input="value => onInputSize('potential_size', value)"
                @change="updateSetting(true)"
              />
            </div>
          </div>
          <div class="size">
            <p>Производительность (ось X)</p>
            <div style="margin-top: 10px">
              <el-input
                placeholder="Ось X"
                v-model.number="matrix.setting.productivity_size"
                style="width: 100px"
                :min="1"
                :max="5"
                type="number"
                @input="value => onInputSize('productivity_size', value)"
                @change="updateSetting(true)"
              />
            </div>
          </div>
        </div>
      </div>
      <div style="margin-top: 50px">
        <h3>Настройка матрицы</h3>
        <p>
          Вы можете менять местами ячейки просто перетаскивая их.
        </p>
        <preview-talent-matrix
          @update-matrix="updateMatrix()"
          :matrix="matrix"
        ></preview-talent-matrix>
      </div>
    </div>
    <talent-matrix-axis-user-values-dialog
      @update-matrix="updateMatrix()"
      ref="showAxisUserValuesDialog"
    >
    </talent-matrix-axis-user-values-dialog>
  </div>
</template>

<script>
import EvalEvaluationsSelect from "@/modules/evaluation/components/EvalEvaluationsSelect.vue";
import PreviewTalentMatrix from "@/modules/talentMatrix/components/PreviewTalentMatrix.vue";
import TalentMatrixAxisFormulaSelect from "@/modules/talentMatrix/components/TalentMatrixAxisFormulaSelect.vue";
import TalentMatrixAxisUserValuesDialog from "@/modules/talentMatrix/components/TalentMatrixAxisUserValuesDialog.vue";
import ShareTalentMatrixUsersTable from "@/modules/talentMatrix/components/ShareTalentMatrixUsersTable.vue";
import ImportModal from "@/components/exportImport/ImportModal.vue";

export default {
  name: 'talent-matrix-setting',
  components: {
    ImportModal,
    EvalEvaluationsSelect,
    TalentMatrixAxisFormulaSelect,
    TalentMatrixAxisUserValuesDialog,
    PreviewTalentMatrix,
    ShareTalentMatrixUsersTable,
  },
  props: {
    loading: false,
    matrix: {
      type: Object,
      default: {},
    },
  },
  methods: {
    openAxisUserValuesDialog(type) {
      this.$refs.showAxisUserValuesDialog.open(type);
    },
    updateSetting(updateMatrixData = false) {
      this.$api.talentMatrix.matrixSettingUpdate({
        ...this.matrix.setting
      })
      .finally(() => {
        if (updateMatrixData) {
          this.$emit('update-matrix-data');
        }
      });
    },
    onInputSize(field, value) {
      let digits = parseInt(String(value).replace(/\D/g, ''), 10);
      if (!isNaN(digits)) {
        this.matrix.setting[field] = Math.min(Math.max(digits, 1), 5);
      } else {
        this.matrix.setting[field] = null;
      }
    },
    updateMatrix() {
      this.$emit('update-matrix');
    },
    importSucceed() {
      this.updateMatrix();
      this.$notify.success({
        title: 'Завершено',
        message: 'Импорт завершен успешно'
      });
    },
  },
}
</script>

<style scoped>
  .talent-matrix-setting {
    .setting-size,
    .setting-axis {
      display: flex;
      justify-content: flex-start;
    }

    .setting-size .size:last-child,
    .setting-axis .axis:last-child {
      margin-left: 50px;
    }

    .axis {
      width: 400px;
    }
  }
</style>